export interface SoundsLibrarySerpSnippetVariant {
  id: string
  label: string
  queryFocus: string[]
  titleTemplate: string
  descriptionTemplate: string
}

export interface SoundsLibraryAboveFoldCopyOption {
  id: string
  label: string
  queryFocus: string[]
  leadTemplate: string
}

const DESCRIPTION_COUNT_TOKEN = '{countPrefix}'
const HEADER_COUNT_TOKEN = '{libraryCount}'
const TITLE_COUNT_TOKEN = '{countPrefix}'

export const SOUNDS_LIBRARY_SERP_SNIPPET_VARIANTS: SoundsLibrarySerpSnippetVariant[] = [
  {
    id: 'download-library-v2',
    label: 'Exact library/download winner with LockChime phrasing',
    queryFocus: ['tesla lock sounds library', 'tesla lock sound library', 'tesla lock sounds', 'tesla wav sounds', 'wav sounds for tesla'],
    titleTemplate: 'Tesla Lock Sounds & Chimes Library: Free {countPrefix}Downloads',
    descriptionTemplate:
      'Tesla lock and locking sounds library: preview {countPrefix}free LockChime.wav files by model, category, or collection, then download and install by USB.',
  },
  {
    id: 'preview-every-sound-v1',
    label: 'Preview-every-sound fallback for truncation resilience',
    queryFocus: ['free tesla lock sounds', 'preview tesla lock sounds', 'tesla sound library'],
    titleTemplate: 'Tesla Lock Sounds Library | Preview Every Free LockChime.wav',
    descriptionTemplate:
      'Preview {countPrefix}free Tesla lock sounds by model, category, or collection, then keep the LockChime.wav download that actually sounds right before you walk away.',
  },
  {
    id: 'library-usb-guide-v2',
    label: 'Library variant with WAV + USB guide support',
    queryFocus: ['lockchime.wav', 'tesla lockchime', 'tesla boombox sounds download', 'tesla boombox sounds download free'],
    titleTemplate: 'Tesla Lock Sounds Library | Free WAV Downloads + USB Guide',
    descriptionTemplate:
      'Compare {countPrefix}Tesla-ready WAVs, open direct LockChime.wav downloads, and move from preview to USB install without renaming files.',
  },
]

export const SOUNDS_LIBRARY_ABOVE_FOLD_COPY_OPTIONS: SoundsLibraryAboveFoldCopyOption[] = [
  {
    id: 'library-download-header-v2',
    label: 'Header lead for exact library/download intent',
    queryFocus: ['tesla lock sounds download free', 'free tesla lock sounds', 'tesla lock sounds library'],
    leadTemplate:
      'Looking for Tesla lock sounds and chimes? Preview and compare {libraryCount} free LockChime.wav downloads by category, collection, or model, then keep the one that fits your car.',
  },
]

export const RECOMMENDED_SOUNDS_LIBRARY_SERP_SNIPPET_VARIANT_ID = 'download-library-v2'
export const ACTIVE_SOUNDS_LIBRARY_SERP_SNIPPET_VARIANT_ID = RECOMMENDED_SOUNDS_LIBRARY_SERP_SNIPPET_VARIANT_ID
export const RECOMMENDED_SOUNDS_LIBRARY_ABOVE_FOLD_COPY_OPTION_ID = 'library-download-header-v2'

function normalizeDescription(template: string, countLabel: string): string {
  const countPrefix = countLabel ? `${countLabel} ` : ''
  return template.replace(DESCRIPTION_COUNT_TOKEN, countPrefix).replace(/\s+/g, ' ').trim()
}

function normalizeTitle(template: string, countLabel: string): string {
  const countPrefix = countLabel ? `${countLabel} ` : ''
  return template.replace(TITLE_COUNT_TOKEN, countPrefix).replace(/\s+/g, ' ').trim()
}

function normalizeHeaderLead(template: string, countLabel: string): string {
  return template.replace(HEADER_COUNT_TOKEN, countLabel).replace(/\s+/g, ' ').trim()
}

export function getActiveSoundsLibrarySerpSnippet(
  countLabel: string,
): SoundsLibrarySerpSnippetVariant & { title: string; description: string } {
  const fallbackVariant = SOUNDS_LIBRARY_SERP_SNIPPET_VARIANTS[0]
  const activeVariant = SOUNDS_LIBRARY_SERP_SNIPPET_VARIANTS.find(
    (variant) => variant.id === ACTIVE_SOUNDS_LIBRARY_SERP_SNIPPET_VARIANT_ID,
  ) ?? fallbackVariant

  return {
    ...activeVariant,
    title: normalizeTitle(activeVariant.titleTemplate, countLabel),
    description: normalizeDescription(activeVariant.descriptionTemplate, countLabel),
  }
}

export function getRecommendedSoundsLibraryAboveFoldCopyOption(
  countLabel: string,
): SoundsLibraryAboveFoldCopyOption & { lead: string } {
  const fallbackOption = SOUNDS_LIBRARY_ABOVE_FOLD_COPY_OPTIONS[0]
  const recommendedOption = SOUNDS_LIBRARY_ABOVE_FOLD_COPY_OPTIONS.find(
    (option) => option.id === RECOMMENDED_SOUNDS_LIBRARY_ABOVE_FOLD_COPY_OPTION_ID,
  ) ?? fallbackOption

  return {
    ...recommendedOption,
    lead: normalizeHeaderLead(recommendedOption.leadTemplate, countLabel || 'free'),
  }
}
